import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Send } from "lucide-react";

const DEFAULT_SUBJECT = "Sei domande per scoprire dove si perde più tempo — bastano 6 minuti";
const buildMessage = (name) => `Ciao${name ? ` ${name}` : ""},

ti mando il link al nostro questionario Discovery: sei domande su strumenti, processi e dove si perde più tempo in azienda. Bastano 6–8 minuti, anche dal telefono, senza registrazione.

Alla fine vedi subito il tuo "indice di manualità" — e io ti ricontatto con un'analisi personalizzata.

A presto,
Mara — Wattline`;

/** Invio del link Discovery via email (Resend). `assessment` null = chiuso. */
export const InviteDialog = ({ assessment, onClose, onSent }) => {
  const [to, setTo] = useState("");
  const [name, setName] = useState("");
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [message, setMessage] = useState(buildMessage(""));
  const [busy, setBusy] = useState(false);
  const editedRef = useRef(false);

  useEffect(() => {
    if (assessment) {
      setTo(""); setName(""); setSubject(DEFAULT_SUBJECT);
      setMessage(buildMessage("")); editedRef.current = false;
    }
  }, [assessment]);

  if (!assessment) return null;

  const onNameChange = (v) => {
    setName(v);
    if (!editedRef.current) setMessage(buildMessage(v.trim()));
  };

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/discovery/assessments/${assessment.id}/invite`, {
        to: to.trim(),
        name: name.trim() || null,
        subject: subject.trim(),
        message,
        link: `${window.location.origin}/d/${assessment.slug_token}`,
      });
      toast.success(`Email inviata a ${to.trim()}`);
      onSent?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Invio non riuscito");
    } finally {
      setBusy(false);
    }
  };

  const emailOk = /^\S+@\S+\.\S{2,}$/.test(to.trim());

  return (
    <Dialog open={!!assessment} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-xl bg-surface sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="discovery-invite-dialog">
        <DialogHeader>
          <DialogTitle>Invita via email — {assessment.title}</DialogTitle>
          <DialogDescription>
            Il bottone «Inizia il questionario» con il link viene aggiunto automaticamente in fondo all'email.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-text-secondary">Email destinatario *</Label>
              <Input value={to} type="email" onChange={(e) => setTo(e.target.value.trim())} maxLength={200}
                     placeholder="nome@azienda.it" className="mt-1.5 h-10 rounded-md border-border bg-surface"
                     data-testid="invite-to-input" />
            </div>
            <div>
              <Label className="text-xs font-medium text-text-secondary">Nome (per il saluto)</Label>
              <Input value={name} onChange={(e) => onNameChange(e.target.value)} maxLength={100}
                     placeholder="es. Laura" className="mt-1.5 h-10 rounded-md border-border bg-surface"
                     data-testid="invite-name-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium text-text-secondary">Oggetto</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200}
                   className="mt-1.5 h-10 rounded-md border-border bg-surface" data-testid="invite-subject-input" />
          </div>
          <div>
            <Label className="text-xs font-medium text-text-secondary">Messaggio</Label>
            <textarea value={message} rows={9} maxLength={4000}
                      onChange={(e) => { editedRef.current = true; setMessage(e.target.value); }}
                      className="wl-input mt-1.5 w-full resize-y text-sm leading-relaxed min-h-[190px]"
                      data-testid="invite-message-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="invite-cancel-btn">Annulla</Button>
          <Button onClick={send}
                  disabled={busy || !emailOk || subject.trim().length < 2 || message.trim().length < 2}
                  data-testid="invite-send-btn" className="bg-accent hover:bg-accent-strong text-white">
            <Send size={14} className="mr-1.5" /> {busy ? "Invio…" : "Invia email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
